import useProductQueries from '@/dashboard/hooks/queries/useProductQueries';
import { pageKey, productKey } from '@/dashboard/lib/queryKeys';
import usePage from '@dashboard/hooks/usePage';
import { useQueryClient, useMutation, useQuery } from '@tanstack/react-query';
import draftToNestedPrice from './lib/draftToNestedPrice';

/**
 * Conversion choke point (spec §7, defense-in-depth): every price-carrying
 * payload is normalized to the nested shape here, regardless of who calls
 * mutate — so the page query cache (which the preview renders from) and the
 * API can never receive a flat price. No-op for already-nested payloads;
 * today every price-carrying payload already flows through the
 * ProductDetailsForm submit mapping, so this guards FUTURE callers.
 */
export function normalizePricePayload(values) {
  if (!values || !Array.isArray(values.prices)) return values;
  return { ...values, prices: values.prices.map(draftToNestedPrice) };
}

/**
 * Product data and mutations.
 *
 * With no argument it manages the PAGE's product, reading and optimistically
 * patching it inside `pageKey(pageId)` — the shape every Product-tab form has
 * always used.
 *
 * Given a `productId` it manages that product standalone, under
 * `productKey(productId)`. Order-bump products belong to no page, so their
 * forms take this branch; everything downstream (the media, files, inventory
 * and description forms) is identical either way.
 *
 * @param {string} [productId]
 */
export default function useProduct(productId) {
  const { page } = usePage();
  const {
    getProduct,
    updateProduct: updateProductMutation,
    attachProductFile,
    detachProductFile,
    attachProductMedia,
    detachProductMedia,
  } = useProductQueries();
  const queryClient = useQueryClient();

  const isStandalone = Boolean(productId);
  const cacheKey = isStandalone ? productKey(productId) : pageKey(page?._id);

  const standaloneQuery = useQuery({
    queryKey: productKey(productId),
    queryFn: () => getProduct(productId),
    enabled: isStandalone,
  });

  const product = isStandalone ? standaloneQuery.data : page.product;
  const targetProductId = isStandalone ? productId : page?.product?._id;

  // The page cache nests the product; the standalone cache IS the product.
  const patchProduct = patch =>
    queryClient.setQueryData(cacheKey, old => {
      if (!old) return old;
      return isStandalone
        ? { ...old, ...patch }
        : { ...old, product: { ...old.product, ...patch } };
    });

  // fetcher already unwraps the `{ data }` envelope, so the product is the response.
  function setProductFilesFromResponse(updated) {
    if (!updated?.files) return;
    patchProduct({ files: updated.files });
  }

  function setProductMediaFromResponse(updated) {
    if (!updated?.media) return;
    patchProduct({ media: updated.media });

    // A bump product's thumbnail rides the page's resolved bump payload, not
    // the product cache the patch above writes, so the preview keeps the old
    // image until this lands. Same blanket key as the update save below.
    if (isStandalone) queryClient.invalidateQueries({ queryKey: ['pages'] });
  }

  const attachFile = useMutation({
    mutationFn: fileId => attachProductFile(targetProductId, fileId),
    onSuccess: setProductFilesFromResponse,
  });

  const detachFile = useMutation({
    mutationFn: fileId => detachProductFile(targetProductId, fileId),
    onSuccess: setProductFilesFromResponse,
  });

  const attachMedia = useMutation({
    mutationFn: fileId => attachProductMedia(targetProductId, fileId),
    onSuccess: setProductMediaFromResponse,
  });

  const detachMedia = useMutation({
    mutationFn: fileId => detachProductMedia(targetProductId, fileId),
    onSuccess: setProductMediaFromResponse,
  });

  const updateProduct = useMutation({
    // Serialize product saves so a slower in-flight save (e.g. committing a price)
    // can't land after — and clobber — a newer one (e.g. a drag-reorder). react-query
    // runs same-scope mutations one at a time, so writes apply in submit order.
    scope: { id: `product-update-${targetProductId ?? 'new'}` },
    mutationFn: rawValues => {
      const values = normalizePricePayload(rawValues);
      if (values.isNew) {
        return updateProductMutation(values);
      }

      const updateTimestamp = Date.now();
      return updateProductMutation(
        {
          ...values,
          _lastUpdateTimestamp: updateTimestamp,
        },
        targetProductId,
      );
    },
    onMutate: async rawValues => {
      const values = normalizePricePayload(rawValues);
      if (values.isNew) {
        return values;
      }

      await queryClient.cancelQueries({ queryKey: cacheKey });

      const previousData = queryClient.getQueryData(cacheKey);

      const updateTimestamp = values._lastUpdateTimestamp || Date.now();

      queryClient.setQueryData(cacheKey, old => {
        if (old?._lastUpdateTimestamp && old._lastUpdateTimestamp > updateTimestamp) {
          return old;
        }

        if (isStandalone) {
          return { ...old, ...values, _lastUpdateTimestamp: updateTimestamp };
        }

        return {
          ...old,
          product: { ...old.product, ...values },
          _lastUpdateTimestamp: updateTimestamp,
        };
      });

      return {
        previousData,
        updateTimestamp,
      };
    },
    onSuccess: result => {
      // updateProduct resolves with { data, error } even on a validation
      // failure, so onSuccess fires for errors too. Don't refetch on error:
      // a refetch + enableReinitialize would reset the form to the server's
      // unchanged product and wipe the user's edits before they can fix the
      // invalid field (QA #18). The optimistic cache keeps their edits visible.
      if (result?.error) return;

      if (isStandalone) {
        queryClient.invalidateQueries({ queryKey: cacheKey });
        // A bump product is shared, so its placements' resolved copy and price
        // are stale on every page listing it. The blanket key is deliberate: it
        // prefix-matches pageKey(id) and pageOrderBumpsKey(id) for every page,
        // not just this one.
        queryClient.invalidateQueries({ queryKey: ['pages'] });
        // The attach picker lists products by title, so a rename leaves it stale.
        queryClient.invalidateQueries({ queryKey: ['products'] });
        return;
      }

      if (page._id) {
        queryClient.invalidateQueries({ queryKey: pageKey(page._id) });
        // The preview's "renews on" proration query is configured to never
        // refetch (refetchOnMount:false) and the selected-price card is seeded
        // from product.proration via initialData, so a billing-anchor edit would
        // otherwise leave it showing the pre-save date. Force a refetch now that
        // the save has committed. Key prefix matches prorationCalculationQueryKey
        // (@repo/checkout queryKeys.js).
        queryClient.invalidateQueries({ queryKey: ['proration'] });
      }
    },
    onError: (error, values, rollback) => {
      if (rollback) rollback();
    },
    retry: 1,
  });

  return {
    product,
    isLoading: isStandalone ? standaloneQuery.isLoading : false,
    // Without this a deep link to a deleted or foreign product id renders a
    // blank panel forever, since `product` simply stays undefined.
    isError: isStandalone ? standaloneQuery.isError : false,
    updateProduct,
    attachFile,
    detachFile,
    attachMedia,
    detachMedia,
  };
}
